import { Box, Icon, Skeleton, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';

import { AgentPriceCard as AgentPriceCardType } from '@/schemas/sceneDataSchema';
import { assertNever } from '@/utils/assertNever';
import useModal from '@/hooks/use-modals';
import { Price } from './Price';
import { ComponentsTypes } from '@/constants/componentsTypes';
import {
  TypographyAlign,
  TypographyColors,
  TypographyFontWeight,
  TypographyVariants,
} from '@/constants/typographyTypes';
import NumberFlow from '@number-flow/react';
import { LinkTypes } from '@/constants/linkTypes';
import { ModalLink } from './formElements/typographies/ModalLink';
import { StyledChip as TermChip } from './formElements/selects/BaseChipElement';
import { SteppedLoader } from '../SteppedLoader';
import { SteppedLoaderImageKeys } from '@/constants/loaderTypes';

const TERMS = ['Monthly', '6 months'] as const;

// Top level state
// Passed into each respective card component
interface EstimatePriceCardState {
  state: 'estimate';
  openPriceEstimateModal: () => void;
  dueTodayAmountInDollars?: number;
  payment_details_modal_id: string;
  shouldShowTermSelector: boolean;
  termSelection: (typeof TERMS)[number];
  onChangeTermSelection: (newTerm: (typeof TERMS)[number]) => void;
}

interface FinalizingPriceCardState {
  state: 'finalizing';
}

interface BindablePriceCardState {
  state: 'bindable';
  openPriceEstimateModal: () => void;
  dueTodayAmountInDollars?: number;
  payment_details_modal_id: string;
  shouldShowTermSelector: boolean;
  termSelection: (typeof TERMS)[number];
  onChangeTermSelection: (newTerm: (typeof TERMS)[number]) => void;
}

type PriceCardState = EstimatePriceCardState | FinalizingPriceCardState | BindablePriceCardState;

// Top Level Component. Responsible for mapping data, rendering the appropriate price card based on state, and callback logic
export const AgentPriceCard = ({ component }: { component: AgentPriceCardType }) => {
  const { getModal } = useModal();
  const priceEstimateModal = getModal(component.price_estimate_modal_id ?? '');

  const assemblePriceCardState = (quoteKind: string, quoteStatus: string): PriceCardState => {
    if (['bindable', 'finalized'].includes(quoteKind.toLowerCase()) && quoteStatus === 'quoted') {
      return {
        state: 'bindable',
        openPriceEstimateModal: () => priceEstimateModal?.open(),
        dueTodayAmountInDollars: 12,
        payment_details_modal_id: component.payment_details_modal_id ?? '',
        shouldShowTermSelector: true,
        termSelection: 'Monthly',
        onChangeTermSelection: () => {},
      };
    } else if (['bindable', 'finalized'].includes(quoteKind.toLowerCase()) && quoteStatus === 'quoting') {
      return {
        state: 'finalizing',
      };
    } else {
      return {
        state: 'estimate',
        openPriceEstimateModal: () => priceEstimateModal?.open(),
        payment_details_modal_id: component.payment_details_modal_id ?? '',
        shouldShowTermSelector: true,
        termSelection: 'Monthly',
        onChangeTermSelection: () => {},
      };
    }
  };

  // TODO: this should be entirely derived state
  const [priceCardState, setPriceCardState] = useState<PriceCardState>(assemblePriceCardState('', ''));

  // TODO: Remove the following debug logging and key event listener
  useEffect(() => {
    const handlekeydownEvent = (event: KeyboardEvent) => {
      if (event.key === '1') {
        setPriceCardState(assemblePriceCardState('', ''));
      }
      if (event.key === '2') {
        setPriceCardState(assemblePriceCardState('bindable', 'quoting'));
      }
      if (event.key === '3') {
        setPriceCardState(assemblePriceCardState('bindable', 'quoted'));
      }
    };

    document.addEventListener('keyup', handlekeydownEvent);
    return () => {
      document.removeEventListener('keyup', handlekeydownEvent);
    };
  }, []);
  // END TODO

  return ((state: PriceCardState) => {
    switch (state.state) {
      case 'estimate':
        return (
          <EstimatePriceCard
            key='estimate'
            {...state}
          />
        );
      case 'finalizing':
        return <FinalizingPriceCard key='finalizing' />;
      case 'bindable':
        return (
          <BindablePriceCard
            key='bindable'
            {...state}
          />
        );
      default:
        return assertNever(state);
    }
  })(priceCardState);
};

// Individual Price Card Components
const EstimatePriceCard = (state: EstimatePriceCardState) => (
  <StyledFadeStack spacing={0}>
    <PriceEstimateTitle
      text='Price Estimate'
      openPriceEstimateModal={state.openPriceEstimateModal}
    />
    <PriceWhenAvailable />
    <Subtitle text='Add as many details as you can for the most accurate price estimate.' />
    <DueTodayRow
      dueTodayAmountInDollars={state.dueTodayAmountInDollars}
      payment_details_modal_id={state.payment_details_modal_id}
    />
    <TermSelector {...state} />
  </StyledFadeStack>
);

const FinalizingPriceCard = () => (
  <StyledFadeStack spacing={0}>
    <Typography variant={TypographyVariants.H3}>Hang tight, we’re calculating your final price...</Typography>
    <StyledSteppedLoaderContainer>
      <SteppedLoader
        steps={[
          {
            type: ComponentsTypes.STEP,
            text: 'Reviewing driver and vehicle details...',
            image: SteppedLoaderImageKeys.DOCS,
            duration: 3000,
          },
          {
            type: ComponentsTypes.STEP,
            text: 'Checking a few driver reports',
            image: SteppedLoaderImageKeys.CAR,
            duration: Number.MAX_SAFE_INTEGER,
          },
        ]}
      />
    </StyledSteppedLoaderContainer>
  </StyledFadeStack>
);

const BindablePriceCard = (state: BindablePriceCardState) => (
  <StyledFadeStack spacing={0}>
    <PriceEstimateTitle
      text='Bindable price'
      openPriceEstimateModal={state.openPriceEstimateModal}
    />
    <PriceWhenAvailable />
    <Subtitle text='Add as many details as you can for the most accurate price estimate.' />
    <DueTodayRow
      dueTodayAmountInDollars={state.dueTodayAmountInDollars}
      payment_details_modal_id={state.payment_details_modal_id}
    />
    <TermSelector {...state} />
  </StyledFadeStack>
);

// Sub Components used in the Price Cards, alphabetized
const DueTodayRow = ({
  dueTodayAmountInDollars,
  payment_details_modal_id,
}: {
  dueTodayAmountInDollars?: number;
  payment_details_modal_id: string;
}) => {
  if (dueTodayAmountInDollars) {
    return (
      <StyledDueTodayRow>
        <Typography variant='h4'>
          <NumberFlow
            value={dueTodayAmountInDollars}
            format={{ style: 'currency', currency: 'USD' }}
            suffix=' Due today'
            spinTiming={{ duration: 750, easing: 'ease-in-out' }}
            opacityTiming={{ duration: 350, easing: 'ease-in-out' }}
            willChange
          />
        </Typography>
        <ModalLink
          component={{
            type: LinkTypes.MODAL,
            text: 'See details',
            method_type: 'open',
            modal_id: payment_details_modal_id,
            color: TypographyColors.TEXT_PRIMARY,
            align: TypographyAlign.LEFT,
            variant: TypographyVariants.BODY2,
            font_weight: TypographyFontWeight.REGULAR,
          }}
        />
      </StyledDueTodayRow>
    );
  } else {
    return (
      <StyledDueTodayRow>
        <Skeleton
          variant='rounded'
          width='100%'
          height={27}
          animation='wave'
        />
      </StyledDueTodayRow>
    );
  }
};

const PriceEstimateTitle = ({ text, openPriceEstimateModal }: { text: string; openPriceEstimateModal: () => void }) => (
  <StyledClickableBox onClick={openPriceEstimateModal}>
    <Typography variant='body2'>{text}</Typography>
    <StyledIcon>info_outline</StyledIcon>
  </StyledClickableBox>
);

const PriceWhenAvailable = () => (
  <Price
    component={{
      type: ComponentsTypes.PRICE,
      variant: TypographyVariants.H1,
    }}
  />
);

const Subtitle = ({ text }: { text: string }) => (
  <Typography
    variant='body2'
    color='textSecondary'
  >
    {text}
  </Typography>
);

const TermSelector = ({
  termSelection,
  onChangeTermSelection,
}: {
  termSelection: (typeof TERMS)[number];
  onChangeTermSelection: (newTerm: (typeof TERMS)[number]) => void;
}) => (
  <StyledChipContainer>
    {TERMS.map((term) => (
      <TermChip
        label={term}
        icon={termSelection === term ? <StyledCheckIcon color='primary'>check</StyledCheckIcon> : undefined}
        selected={termSelection === term}
        size='small'
        variant='outlined'
        onClick={() => onChangeTermSelection(term)}
      />
    ))}
  </StyledChipContainer>
);

// Styled Components, alphabetized
const StyledCheckIcon = styled(Icon)`
  & > .MuiIcon-root {
    height: 24px;
    width: 24px;
  }
`;

const StyledChipContainer = styled('div')`
  display: flex;
  align-items: center;
  align-content: center;
  gap: 8px var(--3, 8px);
  align-self: stretch;
  flex-wrap: none;
`;

const StyledClickableBox = styled(Box)`
  cursor: pointer;
  user-select: none;

  display: flex;
`;

const StyledDueTodayRow = styled('div')`
  display: flex;
  align-items: center;
  gap: var(--3, 8px);
  align-self: stretch;
  margin-top: var(--5, 16px);
  margin-bottom: var(--6, 20px);
`;

const StyledFadeStack = styled(Stack)`
  animation: fadeIn 0.2s;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const StyledIcon = styled(Icon)`
  font-family: 'Material Symbols Outlined';
  font-size: 12px;
  margin-left: 2px;
  align-self: center;
`;

const StyledSteppedLoaderContainer = styled('div')`
  margin-top: var(--6, 20px);
`;
